import { Link } from "react-router-dom";
import { NeptuneLogo } from "./NeptuneMark";
import { useContent } from "../../context/ContentContext";
import { LEGAL_PAGES, legalPathFor } from "../../lib/legal";

const fallbackCols = {
  solutions: [["Gateway", "/solutions"], ["Acquiring", "/solutions"], ["Crypto", "/solutions"], ["Routing", "/solutions"], ["Payouts", "/solutions"], ["Analytics", "/solutions"], ["Settlement", "/solutions"]],
  industries: [["iGaming", "/industries"], ["Ecommerce", "/industries"], ["SaaS", "/industries"], ["Forex", "/industries"], ["High-risk", "/industries"]],
  about: [["Manifesto", "/about"], ["Why Neptune", "/about"], ["Get in touch", "/contact"]],
};

function asPairs(items, fallback) {
  if (!Array.isArray(items)) return fallback;
  return items.map((it) =>
    Array.isArray(it) ? it : [it.label, it.to || "#"]
  );
}

export default function Footer() {
  const { t, content } = useContent();

  // Dynamically build the "Legal" column from the registry, filtered by visibility flag.
  const legalLinks = LEGAL_PAGES
    .filter((p) => {
      const v = content[`legal.${p.slug}.visible`];
      // Default to true when the flag is missing so a freshly-seeded site shows links immediately.
      // Coerce: accept boolean true OR string 'true' to be defensive against any non-standard writes.
      if (v === undefined) return true;
      if (typeof v === "boolean") return v;
      if (typeof v === "string") return v.toLowerCase() === "true";
      return Boolean(v);
    })
    .map((p) => [
      t(`legal.${p.slug}.nav_label`, p.shortLabel),
      legalPathFor(p.slug),
    ]);

  const cols = [
    { title: "Solutions", links: asPairs(t("footer.col_solutions"), fallbackCols.solutions) },
    { title: "Industries", links: asPairs(t("footer.col_industries"), fallbackCols.industries) },
    { title: "About", links: asPairs(t("footer.col_about"), fallbackCols.about) },
    { title: "Legal", links: legalLinks },
  ];

  return (
    <footer
      data-testid="footer"
      className="relative bg-[#070707] border-t border-white/[0.05] pt-24 pb-10 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center">
              <NeptuneLogo height={26} />
            </Link>
            <p className="mt-4 text-[13px] text-zinc-500 leading-relaxed max-w-xs">
              {t("footer.tagline", "Payment orchestration for businesses entering a new era.")}
            </p>
          </div>
          {cols.map((c) => (
            <div key={c.title}>
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                {c.title}
              </div>
              <ul className="mt-4 space-y-2.5">
                {c.links.map(([label, to], i) => (
                  <li key={i}>
                    <Link
                      to={to}
                      data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="text-[13px] text-zinc-400 hover:text-white transition-colors"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-20 pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[12px] text-zinc-500">
          <div>© {new Date().getFullYear()} {t("footer.copyright", "Neptune Pay. All rights reserved.")}</div>
          <div className="flex items-center gap-4 text-zinc-500">
            <span>{t("footer.compliance_line", "neptunepay.io · PCI-DSS Level 1 · SOC 2 Type II")}</span>
          </div>
        </div>

        {/* Feb 27 2026 — Canadian MSB (FINTRAC) compliance notice.
           Content-driven via two i18n keys so ops can edit copy without
           a code deploy. Uses `{regNo}` and `{registryLink}` placeholders
           in the sentence so the anchor tags land at the right positions
           regardless of translation. */}
        <FINTRACNotice t={t} />
      </div>
    </footer>
  );
}

function FINTRACNotice({ t }) {
  const FINTRAC_REGISTRY_URL =
    "https://www10.fintrac-canafe.gc.ca/msb-esm/public/msb-search/search-by-name/";
  const msbNo = t("footer.msb_registration_no", "C100000902");
  const sentence = t(
    "footer.regulatory_notice",
    "Money Services Business (MSB) services in Canada are provided by " +
    "FLOWA PAY INC., a Money Services Business registered with FINTRAC " +
    "(the Financial Transactions and Reports Analysis Centre of Canada). " +
    "NeptunePay's technology platform and payment gateway are operated by " +
    "its United Kingdom entity, which is not itself registered with FINTRAC. " +
    "MSB registration no. {regNo}. {registryLink}.",
  );

  // Split the sentence on the two placeholders and interleave anchor tags
  // at their positions. Fallback: if the string somehow doesn't contain
  // the placeholders (e.g. bad locale edit), render as plain text so we
  // never lose the compliance content.
  const parts = sentence.split(/(\{regNo\}|\{registryLink\})/g);
  const linkClass =
    "text-zinc-500 underline decoration-zinc-700 underline-offset-2 " +
    "hover:text-white hover:decoration-zinc-400 transition-colors";

  return (
    <div
      className="mt-6 pt-4 border-t border-white/[0.04]"
      data-testid="footer-fintrac-notice"
    >
      <p className="max-w-4xl text-[11px] leading-relaxed text-zinc-600">
        {parts.map((seg, i) => {
          if (seg === "{regNo}") {
            return (
              <a
                key={i}
                href={FINTRAC_REGISTRY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-fintrac-regno-link"
                className={linkClass}
              >
                {msbNo}
              </a>
            );
          }
          if (seg === "{registryLink}") {
            return (
              <a
                key={i}
                href={FINTRAC_REGISTRY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-fintrac-registry-link"
                className={linkClass}
              >
                Verify on the FINTRAC MSB registry
              </a>
            );
          }
          return <span key={i}>{seg}</span>;
        })}
      </p>
    </div>
  );
}
